
import React, { type Node } from 'react';
import { useSelector } from 'react-redux';

import { Avatar } from '../../base/avatar';
import {
    Icon,
    IconCameraEmpty,
    IconCameraEmptyDisabled,
    IconCrown,
    IconDominantSpeaker,
    IconMicrophoneEmpty,
    IconMicrophoneEmptySlash,
    IconNetwork,
    IconRaisedHandHollow
} from '../../base/icons';
import { getParticipantDisplayNameWithId } from '../../base/participants';
import { Tooltip } from '../../base/tooltip';
import ParticipantActionEllipsis from '../../breakout-rooms/components/web/ParticipantActionEllipsis';
import ConnectionIndicator from '../../connection-indicator/components/web/ConnectionIndicator';
import {
    ACTION_TRIGGER,
    type ActionTrigger,
    MEDIA_STATE,
    type MediaState
} from '../constants';

import {
    ColoredIcon,
    ParticipantActionsHover,
    ParticipantActionsPermanent,
    ParticipantContainer,
    ParticipantContent,
    ParticipantDetailsContainer,
    ParticipantName,
    ParticipantNameContainer
} from './styled';

/**
 * Participant actions component mapping depending on trigger type.
 */
const Actions = {
    [ACTION_TRIGGER.HOVER]: ParticipantActionsHover,
    [ACTION_TRIGGER.PERMANENT]: ParticipantActionsPermanent
};

const participantIconStyle = {
    width: '28px',
    height: '28px',
    background: '#e4ebf5',
    borderRadius: '50px'
};

/**
 * Returns the icon mapping for possible participant audio states.
 *
 * @param {Object} participant - The participant object.
 * @returns {React$Element<any> | null}
 */
const getAudioStateIcon = (firstParticipantInList: Boolean, audioMediaState: MediaState): React$Element<any> | null => {
    const icons = {
        [MEDIA_STATE.FORCE_MUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.fMute' }
                position = { firstParticipantInList ? ' bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790'>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 19 }
                            src = { IconMicrophoneEmptySlash } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.MUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.mute' }
                position = { firstParticipantInList ? ' bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790'>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 19 }
                            src = { IconMicrophoneEmptySlash } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.UNMUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.unmute' }
                position = { firstParticipantInList ? ' bottom-right' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790'>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 19 }
                            src = { IconMicrophoneEmpty } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.NONE]: null
    };

    return icons[audioMediaState];
};

/**
 * Returns the icon mapping for possible participant video states.
 *
 * @param {Object} participant - The participant object.
 * @returns {React$Element<any> | null}
 */
const getVideoStateIcon = (firstParticipantInList: Boolean, videoMuteState: MediaState): React$Element<any> | null => {
    const icons = {
        [MEDIA_STATE.FORCE_MUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.videoFMuted' }
                position = { firstParticipantInList ? ' bottom' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790 '>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 16 }
                            src = { IconCameraEmptyDisabled } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.MUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.videoMuted' }
                position = { firstParticipantInList ? ' bottom' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790 '>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 16 }
                            src = { IconCameraEmptyDisabled } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.UNMUTED]: (
            <Tooltip
                className = 'small'
                content = { 'videothumbnail.videoUnmuted' }
                position = { firstParticipantInList ? ' bottom' : 'top-centre' }>
                <div
                    className = 'participantpane-item-icons'
                    style = { participantIconStyle }>
                    <ColoredIcon color = '#4b6790 '>
                        <Icon
                            fillColor = '#4b6790'
                            size = { 16 }
                            src = { IconCameraEmpty } />
                    </ColoredIcon>
                </div>
            </Tooltip>
        ),
        [MEDIA_STATE.NONE]: null
    };

    return icons[videoMuteState];
};

type Props = {

    /**
     * Type of trigger for the participant actions.
     */
    actionsTrigger: ActionTrigger,

    /**
     * Media state for audio.
     */
    audioMediaState: MediaState,

    /**
     * React children.
     */
    children: Node,

    /**
     * Is this item highlighted/raised.
     */
    isHighlighted?: boolean,

    /**
     * Is Participant Moderator.
     */
    isModerator: boolean,


    /**
     * The name of the participant. Used for showing lobby names.
     */
    displayName?: string;

    /**
     * The name of the participant. Used for showing lobby names.
     */
    name?: string,

    /**
     * Callback for when the mouse leaves this component.
     */
    onLeave?: Function,

    /**
     * Participant reference.
     */
    participant: Object,

    /**
     * Media state for video.
     */
    videoMuteState: MediaState,
};

export const ParticipantItem = ({
    children,
    isHighlighted,
    onLeave,
    actionsTrigger = ACTION_TRIGGER.HOVER,
    audioMediaState = MEDIA_STATE.NONE,
    videoMuteState = MEDIA_STATE.NONE,
    name,
    participantList,
    raiseHandP = false,
    displayName: displayNameProp,
    isModerator,
    participant: p,
    hideOptions = false,
    onContextMenu,
    index,
    ariaLabel
}: Props) => {
    const ParticipantActions = Actions[actionsTrigger] || Actions[ACTION_TRIGGER.HOVER];
    const _connectionIndicatorDisabled = useSelector(state => state['features/base/config']?.CONNECTION_INDICATOR_DISABLED);
    const firstParticipantInList = index === 1;
    const lastParticipantInList = index === (raiseHandP ? (participantList || []).filter(participant => participant?.raisedHand === true).length : (participantList || []).length);
    const activeIndicators = (isModerator ? 1 : 0) + (p?.dominantSpeaker ? 1 : 0) + (p?.raisedHand ? 1 : 0);
    const displayName
        = displayNameProp || name || useSelector(getParticipantDisplayNameWithId(p?.id));
    const moderatorStyle = { gap: '7px',
        display: 'flex',
        flexDirection: 'row' };

    return (
        <ParticipantContainer
            isHighlighted = { isHighlighted }
            isLastParticipant = { lastParticipantInList || participantList?.length === 1 }
            isPinned = { p?.pinned }
            onMouseLeave = { onLeave }
            trigger = { actionsTrigger }>
            <Avatar
                className = 'participant-avatar'
                participantId = { p?.id }
                size = { 32 } />
            <ParticipantContent>
                <ParticipantDetailsContainer width = { activeIndicators === 3 ? 50 : activeIndicators === 2 ? 60 : activeIndicators === 1 ? 70 : '' }>
                    <ParticipantNameContainer>
                        <ParticipantName><span
                            title = { displayName }>{displayName}</span></ParticipantName>
                    </ParticipantNameContainer>

                </ParticipantDetailsContainer>
                <div className = 'participant-icon-stack'>
                    <div style = { moderatorStyle }>
                        {!_connectionIndicatorDisabled && p?.connectionStatus && (
                            <ConnectionIndicator
                                StatusIcon = { IconNetwork }
                                _connectionStatus = { p?.connectionStatus }
                                alwaysVisible = { true }
                                enableStatsDisplay = { true }
                                firstParticipantInList = { firstParticipantInList }
                                iconSize = { 16 }
                                isLocalVideo = { p?.isLocal }
                                largeVideo = { false }
                                pLocal = { p?.local }
                                participantId = { p?.id }
                                participantPane = { true }
                                statsPopoverPosition = { 'bottom-left' }
                                tooltipKey = { 'connection indicator' }
                                tooltipPosition = { 'bottom-left' } />
                        )}
                        {p?.dominantSpeaker && (
                            <Tooltip
                                className = 'small'
                                content = { 'videothumbnail.dominentSpeaker' }
                                position = { firstParticipantInList ? ' bottom' : 'top-centre' }>

                                <div className = 'moderator-icon-participants participantpane-item-icons'>
                                    <Icon src = { IconDominantSpeaker } />
                                </div>
                            </Tooltip>
                        )}
                        {isModerator && (
                            <Tooltip
                                className = 'small'
                                content = { 'settings.moderator' }
                                position = { firstParticipantInList ? ' bottom' : 'top-centre' }>
                                <div className = 'moderator-icon-participants participantpane-item-icons'>
                                    <Icon src = { IconCrown } />
                                </div>
                            </Tooltip>
                        )}
                    </div>

                    {p?.raisedHand && <Tooltip
                        className = 'small'
                        content = { 'voicecommand.hints.raiseHand' }
                        position = { firstParticipantInList ? ' bottom' : 'top-centre' }>
                        <div className = 'moderator-icon-participants participantpane-item-icons'><Icon
                            size = { 16 }
                            src = { IconRaisedHandHollow } /></div></Tooltip>}
                    {getVideoStateIcon(firstParticipantInList, videoMuteState)}
                    {getAudioStateIcon(firstParticipantInList, audioMediaState)}
                    {!p?.local && !hideOptions && <ParticipantActionEllipsis
                        accessibilityLabel = { ariaLabel }
                        className = { 'participantActionEllipsis' }
                        onClick = { onContextMenu } />}
                </div>
                {!p?.local && <ParticipantActions children = { children } />}
            </ParticipantContent>
        </ParticipantContainer>
    );
};
